import axios from "@/utils/axios";
import axios2 from "axios";
import useToast from "@/hooks/useToast";

const GREYHOUND_URL = process.env.NEXT_PUBLIC_GREYHOUND_URL;

let uploadAbortController = new AbortController();
export async function uploadToS3(base64:string) {
    uploadAbortController.abort();
    uploadAbortController = new AbortController();
    try {
        let res = await axios.post("/v3/images/upload-imagetoS3-ai-background",{
            imageData: base64
        },{
            signal: uploadAbortController.signal
        });
        return res.data?.data;
    } catch (error) {
        return error;
    }
}

let uploadImageToS3AbortController = new AbortController();
export async function uploadImageToS3(base64:string) {
    uploadImageToS3AbortController.abort();
    uploadImageToS3AbortController = new AbortController();
    try {
        let res = await axios.post("/v3/images/upload-imagetoS3-ai-background",{
            imageData: base64
        },{
            signal: uploadImageToS3AbortController.signal
        });
        return res.data?.data;
    } catch (error) {
        return error;
    }
}


export interface AttributesList {
    fieldName: string,
    values: Array<string>,
    value: string
}


let backgroundsAbortController = new AbortController();
export async function generateBackgroundsVariation({url,prompt,entity_id, background_image, batch_size}) {
    backgroundsAbortController.abort();
    backgroundsAbortController = new AbortController();
    let res = await axios2.post(`${GREYHOUND_URL}/ai_generation`,{
        "url":url,
        "prompt":prompt,
        "batch_size":batch_size,
        "background_image": background_image,
        entity_id,
    },{
        signal: backgroundsAbortController.signal
    });

    return {
        images: res.data?.data,
        prompt: res.data?.prompt
    };
}

let abortController = new AbortController();
export async function createNewTemplate(url:string, baseTemplateId:string) {
    abortController?.abort();
    abortController = new AbortController();
    const res = await axios.post("v3/exploreTemplates/insertSelectedBackgroundVariation",{
        lifestyleImageId: baseTemplateId,
        selectedVariation: url,
        templateType:"DALLE",
        status:"UPDATE"
    },{
        signal: abortController.signal
    });

    return res.data.data;
}

let generateAttributesAbortController = new AbortController();
export async function generateAttributesAndPrompt(url:string) {
    let attributes:AttributesList[] = []
    generateAttributesAbortController?.abort();
    generateBackgroundsAbortController.abort();
    generateAttributesAbortController = new AbortController();
    let res = await axios.post(`v4/batch-upload/generatePrompt`,{
        "imageUrl":url,
    },{
        signal: generateAttributesAbortController.signal
    });

    Object.keys(res?.data?.data?.dropdown || {}).map((key) => {
        const values = res?.data?.data?.dropdown[key];
        attributes.push({
            fieldName: key,
            values: values,
            value: values?.[0]
        });
    })

    return {
        prompt:res?.data?.data?.sentence, 
        attributesList: attributes,
        formats: res?.data?.data?.dropdown,
    };
}


let generateBackgroundsAbortController = new AbortController();

export async function generateBackgrounds(imageId, prompt, backgroundImage, boundingBox, onProgressUpdate,canvasHeight,canvasWidth) {
    generateBackgroundsAbortController.abort();
    generateBackgroundsAbortController = new AbortController();
    
    const requestData = {
        prompt: {
            image: backgroundImage,
            text: prompt,
            weight: 0
        },
        imageId: imageId,
        height: canvasHeight ||2000,
        width: canvasWidth || 2000,
        boundingBox: boundingBox,
    };

    try {
        const response = await axios.post('v4/batch-upload/getBackgrounds', requestData, {
            signal: generateBackgroundsAbortController.signal
        });

        const urls = await handleResponse(response, requestData, onProgressUpdate);
        return urls;

    } catch (error) {
        console.error("Error generating backgrounds:", error);
        throw error;
    }
}

async function handleResponse(response, requestData, onProgressUpdate) {
    let backgrounds = response?.data?.data?.backgrounds || [];

    let urls = backgrounds.map(background => ({
        url: background.superImposedImage || "",
        loading: !background.superImposedImage
    }));

    while (urls.length < 4) {
        urls.push({
            url: "",
            loading: true
        });
    }

    onProgressUpdate([...urls]);

    while (true) {
        const incompleteBackgrounds = urls.filter(b => b.loading);

        if (incompleteBackgrounds.length === 0 || !window.location.href.includes("editor")) {
            break;
        }

        await new Promise(resolve => setTimeout(resolve, 2000));

        const newResponse = await axios.post('v4/batch-upload/getBackgrounds', requestData, {
            signal: generateBackgroundsAbortController.signal
        });

        const updatedBackgrounds = newResponse?.data?.data?.backgrounds || [];

        urls = urls.map((urlObj, index) => {
            const updatedBackground = updatedBackgrounds[index];
            if (updatedBackground && updatedBackground.superImposedImage) {
                return {
                    url: updatedBackground.superImposedImage,
                    loading: false
                };
            }
            return urlObj; 
        });

        onProgressUpdate([...urls]);
    }

    return urls;
}

let generateMoreAbortController = new AbortController();
export async function generateMore(image_id, prompt, job_id, boundingBox, backgroundURL, aiGenPNG, canvasHeight, canvasWidth) {
    generateMoreAbortController.abort();
    generateMoreAbortController = new AbortController();
    let requestData = {
        count: 4,
        prompt: {
            image: backgroundURL,
            text: prompt,
            weight: 0
        },
        boundingBox: boundingBox,
        images: [image_id],
        height: canvasHeight || 2000,
        width: canvasWidth || 2000,
        jobId: job_id,
        dropdown: [],
        aiGenPNG: aiGenPNG,
    }
    const response = await axios.post("v4/batch-upload/generateMore", requestData,{
        signal: generateMoreAbortController.signal
    })
    return response;
}

export async function urlToBinaryFile(url, fileName = 'file.png') {
    try {
        const response = await fetch(url);
        
        if (!response.ok) {
            throw new Error(`Failed to fetch ${url}: ${response.statusText}`);
        }
        
        const arrayBuffer = await response.arrayBuffer();
        const binaryFile = new File([arrayBuffer], fileName, { type: response.headers.get('content-type') });

        return binaryFile;
    } catch (error) {
        console.error("Error converting URL to binary file:", error);
        throw error;
    }
}
